import { Adder } from "../adder";
import { Nub } from "../core";
import type { $ } from "../dom";
import { createEventNotifier } from "../event-notifier";
import { mod } from "../maths";
import { getPagination } from "../pagination";

export type ListOptionsProvisional = {
  readyPages?: number[];

  pageCount?: number;
};

export type ListOptions = {
  page?: number;
  pageSize?: number;

  defaultPage?: number;
  defaultPageSize?: number;

  provisional?: ListOptionsProvisional;

  loop?: boolean;

  loadMore?: {
    adder: Adder;
    scroll?:
      | {
          onScrollIntersecting: () => void;
        }
      | boolean;
  };

  elements?: readonly Element[];
};

export type ListUpdateCallback = () => void;

export class List extends Nub {
  private readonly updateEventNotifier =
    createEventNotifier<ListUpdateCallback>();

  public readonly initialElements: readonly Element[];

  private readonly _defaultPage: number;
  private readonly _defaultPageSize: number;

  private _page: number;
  private _pageSize: number;
  private _loop: boolean;

  private _elements: readonly Element[];

  private _loadMoreAdder: Adder | null = null;
  private _lastObservedElement: Element | null = null;
  private _intersectionObserver: IntersectionObserver | null = null;
  private _shouldRefreshObservedElement: boolean = false;

  private _provisional: {
    readyPages: Set<number>;

    pageCount?: number;
  } | null;

  constructor($root: $, options: ListOptions = {}) {
    super($root, {
      type: "single",
      name: "list",
    });

    const {
      page = 0,
      pageSize = Infinity,
      defaultPage = 0,
      defaultPageSize = pageSize,
      provisional,
      loop = false,
      loadMore,
      elements,
    } = options;

    this._provisional = provisional
      ? {
          readyPages: new Set([page, ...(provisional.readyPages ?? [])]),

          pageCount: provisional.pageCount,
        }
      : null;

    this._defaultPage = defaultPage;
    this._defaultPageSize = defaultPageSize;

    this._page = page;
    this._pageSize = pageSize;
    this._loop = loop;

    this.initialElements = elements ?? this.$root.children.get();
    this._elements = [...this.initialElements];

    if (loadMore) {
      this._loadMoreAdder = loadMore.adder;

      if (loadMore.scroll) {
        const {
          onScrollIntersecting = () => {
            this.loadMore();
          },
        } = typeof loadMore.scroll === "object" ? loadMore.scroll : {};

        this._intersectionObserver = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];

            if (!entry.isIntersecting) {
              return;
            }

            if (!this.canLoadMore) {
              return;
            }

            onScrollIntersecting();
          },
          {
            rootMargin: "200px 0px",
            threshold: 0,
          },
        );
      }
    }

    this.update();
  }

  get page(): number {
    return this._page;
  }

  set page(value: number) {
    if (value < 0 || value >= this.pageCount) {
      return;
    }

    if (this._provisional !== null && !this.isPageReady(value)) {
      return;
    }

    this._page = value;

    this.update();
  }

  get pageSize(): number {
    return this._pageSize;
  }

  set pageSize(value: number) {
    this._pageSize = value;

    this.update();
  }

  get pageCount(): number {
    const computedPageCount =
      this.pageSize === Infinity
        ? 1
        : Math.max(1, Math.ceil(this._elements.length / this.pageSize));

    if (
      this._provisional !== null &&
      this._provisional.pageCount !== undefined
    ) {
      return Math.max(computedPageCount, this._provisional.pageCount);
    }

    return computedPageCount;
  }

  get loop(): boolean {
    return this._loop;
  }

  get pages(): number[] {
    return this._provisional !== null
      ? [...this._provisional.readyPages].sort((a, b) => a - b)
      : Array.from({ length: this.pageCount }, (_, index) => index);
  }

  get canNextPage(): boolean {
    return (
      this.pageCount > 1 && (this._loop || this._page < this.pages.at(-1)!)
    );
  }

  nextPage(update: boolean = true): void {
    const pages = this.pages;
    const pageIndex = pages.indexOf(this.page);

    if (this._loop) {
      this._page = pages.at(mod(pageIndex + 1, pages.length))!;
    } else {
      this._page = pages.at(Math.min(pageIndex + 1, pages.length - 1))!;
    }

    if (update) {
      this.update();
    }
  }

  get canPreviousPage(): boolean {
    return this.pageCount > 1 && (this._loop || this._page > this.pages.at(0)!);
  }

  previousPage(update: boolean = true): void {
    const pages = this.pages;
    const pageIndex = pages.indexOf(this.page);

    if (this._loop) {
      this._page = pages.at(mod(pageIndex - 1, pages.length))!;
    } else {
      this._page = pages.at(Math.max(pageIndex - 1, 0))!;
    }

    if (update) {
      this.update();
    }
  }

  get shownCount(): number {
    if (this.pageSize === Infinity) {
      return this._elements.length;
    }

    const offset = this.getPageOffset(this.page);

    return Math.min(
      this.pageSize,
      this._elements.length - offset * this.pageSize,
    );
  }

  get elementCount(): number {
    return this._elements.length;
  }

  replaceChildren(...elements: Element[]): void {
    this._shouldRefreshObservedElement =
      elements.length > this._elements.length;
    this._elements = elements;

    this.update();
  }

  get canLoadMore(): boolean {
    return this.pageSize < this.elementCount;
  }

  loadMore(update: boolean = true): void {
    if (this.canLoadMore && this._loadMoreAdder) {
      this._pageSize = this._loadMoreAdder.next(
        this.pageSize,
        this.elementCount,
      );

      if (update) {
        this.update();
      }
    }
  }

  get canLoadLess(): boolean {
    return !!this._loadMoreAdder?.hasPastStates;
  }

  loadLess(update: boolean = true): void {
    if (this.canLoadLess && this._loadMoreAdder) {
      this._pageSize = this._loadMoreAdder.previous(this.elementCount);

      if (update) {
        this.update();
      }
    }
  }

  resetPage(update: boolean = true): void {
    this._page = this._defaultPage;
    this._pageSize = this._defaultPageSize;
    this._loadMoreAdder?.reset();

    if (update) {
      this.update();
    }
  }

  clearProvisional(update: boolean = true): void {
    this._provisional = null;

    if (update) {
      this.update();
    }
  }

  getPagination({
    siblingCount,
    withEllipsis,
    includeNotReadyPages = true,
  }: {
    siblingCount?: number;
    withEllipsis?: boolean;
    includeNotReadyPages?: boolean;
  } = {}): number[] {
    return getPagination({
      currentIndex: this.page,
      siblingCount,
      withEllipsis,
      ...(this._provisional === null || includeNotReadyPages
        ? { totalPages: this.pageCount }
        : { validPages: this._provisional.readyPages }),
    });
  }

  isPageReady(pageIndex: number): boolean {
    if (this._provisional === null) {
      return true;
    }

    return this._provisional.readyPages.has(pageIndex);
  }

  markProvisionalPageAsReady(pageIndex: number, update: boolean = true): void {
    if (this._provisional === null) {
      return;
    }

    this._provisional.readyPages.add(pageIndex);

    if (update) {
      this.update();
    }
  }

  onUpdate(callback: () => void): void {
    this.updateEventNotifier.addListener(callback);
  }

  private getPageOffset(pageIndex: number): number {
    if (this._provisional == null) {
      return pageIndex;
    }

    const index = this.pages.indexOf(pageIndex);

    return index === -1 ? -1 : index;
  }

  private update(): void {
    if (this._provisional) {
      if (!this.isPageReady(this.page)) {
        this._page = this.pages.at(0) ?? 0;
      }
    } else {
      if (this._page >= this.pageCount) {
        this._page = Math.max(this.pageCount - 1, 0);
      }
    }

    const offset = this.getPageOffset(this.page);

    const displayedElements = this._elements.slice(
      offset * this.pageSize,
      (offset + 1) * this.pageSize,
    );

    const content = this.$root.first;

    if (content) {
      this.reconcileChildren(content, displayedElements);
    }

    if (this._intersectionObserver) {
      if (this.pageSize === this.elementCount && this._lastObservedElement) {
        this._intersectionObserver?.unobserve(this._lastObservedElement);

        this._lastObservedElement = null;
      } else {
        const lastShownElement = displayedElements.at(-1) ?? null;

        if (lastShownElement !== this._lastObservedElement) {
          if (this._lastObservedElement) {
            this._intersectionObserver.unobserve(this._lastObservedElement);
          }

          this._lastObservedElement = lastShownElement;

          if (this._lastObservedElement) {
            this._intersectionObserver.observe(this._lastObservedElement);
          }
        } else if (
          this._shouldRefreshObservedElement &&
          this._lastObservedElement !== null
        ) {
          this._intersectionObserver.unobserve(this._lastObservedElement);
          this._intersectionObserver.observe(this._lastObservedElement);
        }
      }
    }

    this._shouldRefreshObservedElement = false;

    this.updateEventNotifier.notifyListeners();
  }

  // Mutate only the difference instead of using replaceChildren, which detaches
  // and re-attaches every child (even unchanged ones). Keeping shared nodes in
  // place avoids scroll jumps and preserves their state when the new list is a
  // superset of the current one (load more).
  private reconcileChildren(
    content: Element,
    elements: readonly Element[],
  ): void {
    elements.forEach((element, index) => {
      if (content.children[index] !== element) {
        content.insertBefore(element, content.children[index] ?? null);
      }
    });

    while (content.children.length > elements.length) {
      content.lastElementChild!.remove();
    }
  }
}
